export const VALUE_PROPERTY_TYPES = [
    { type: 'text', label: 'Text', icon: 'fa-font' },
    { type: 'number', label: 'Number', icon: 'fa-hashtag' },
    { type: 'currency', label: 'Currency', icon: 'fa-coins' },
    { type: 'color', label: 'Color', icon: 'fa-palette' },
    { type: 'url', label: 'URL', icon: 'fa-link' },
    { type: 'bulletList', label: 'Bullet list', icon: 'fa-list-ul' },
    { type: 'numberedList', label: 'Numbered list', icon: 'fa-list-ol' },
    { type: 'image', label: 'Image', icon: 'fa-image' },
    { type: 'imageList', label: 'Image gallery', icon: 'fa-images' }
];

export const isTextListType = (type) => type === 'bulletList' || type === 'numberedList';

const slugify = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    || `property_${Date.now()}`;

export const createValueProperty = (type = 'text', existing = []) => {
    const meta = VALUE_PROPERTY_TYPES.find(item => item.type === type) || VALUE_PROPERTY_TYPES[0];
    const used = new Set((existing || []).map(item => item.key));
    let key = slugify(meta.label);
    let suffix = 2;
    while (used.has(key)) {
        key = `${slugify(meta.label)}_${suffix}`;
        suffix += 1;
    }

    return {
        key,
        label: meta.label,
        type: meta.type,
        required: false,
        options: [],
        order: existing.length
    };
};

const normalizeSchemaType = (type) => {
    const normalized = String(type || 'text').trim() || 'text';
    if (normalized === 'enum' || normalized === 'list') return 'bulletList';
    if (normalized === 'boolean') return 'text';
    return normalized;
};

export const normalizeValueSchema = (schema) => {
    if (!Array.isArray(schema)) return [];
    return schema
        .filter(item => item && (item.key || item.Key))
        .map((item, index) => ({
            key: String(item.key ?? item.Key ?? '').trim(),
            label: String(item.label ?? item.Label ?? item.key ?? item.Key ?? '').trim(),
            type: normalizeSchemaType(item.type ?? item.Type ?? 'text'),
            required: Boolean(item.required ?? item.Required),
            options: Array.isArray(item.options || item.Options)
                ? (item.options || item.Options).map(option => String(option)).filter(Boolean)
                : [],
            order: Number.isFinite(Number(item.order ?? item.Order)) ? Number(item.order ?? item.Order) : index
        }))
        .filter(item => item.key)
        .sort((a, b) => a.order - b.order);
};

export const CARD_BUILTIN_BLOCKS = [
    { id: 'title', label: 'Title', icon: 'fa-heading' },
    { id: 'price', label: 'Price', icon: 'fa-coins' },
    { id: 'description', label: 'Description', icon: 'fa-align-left' }
];

export const fieldBlockId = (key) => `field:${key}`;
export const isFieldBlock = (id) => String(id || '').startsWith('field:');
export const fieldKeyFromBlock = (id) => (isFieldBlock(id) ? String(id).slice(6) : '');

const IMAGE_REGIONS = new Set(['left', 'right', 'top', 'none']);

export const normalizeCardLayout = (layout) => {
    if (!layout || typeof layout !== 'object' || Array.isArray(layout)) return null;
    const rawRegion = String(layout.imageRegion ?? layout.ImageRegion ?? 'right').trim().toLowerCase();
    const imageRegion = IMAGE_REGIONS.has(rawRegion) ? rawRegion : 'right';
    const rawBlocks = layout.blocks || layout.Blocks;
    const seen = new Set();
    const blocks = (Array.isArray(rawBlocks) ? rawBlocks : [])
        .map((block) => {
            const id = String(typeof block === 'string' ? block : (block?.id ?? block?.Id ?? '')).trim();
            return id ? { id } : null;
        })
        .filter(Boolean)
        .filter((block) => {
            if (seen.has(block.id)) return false;
            if (block.id !== 'title' && block.id !== 'price' && block.id !== 'description' && !isFieldBlock(block.id)) {
                return false;
            }
            seen.add(block.id);
            return true;
        });
    return { imageRegion, blocks };
};

export const defaultCardLayout = (schema = []) => ({
    imageRegion: 'right',
    blocks: [
        { id: 'title' },
        { id: 'price' },
        { id: 'description' },
        ...normalizeValueSchema(schema)
            .filter(field => field.type !== 'image' && field.type !== 'imageList')
            .map(field => ({ id: fieldBlockId(field.key) }))
    ]
});

export const resolveCardLayout = (layout, schema = []) =>
    normalizeCardLayout(layout) || defaultCardLayout(schema);

export const getFeatureCardLayout = (feature) =>
    normalizeCardLayout(feature?.cardLayout || feature?.property?.cardLayout || feature?.CardLayout || feature?.property?.CardLayout);

export const normalizePropertyValues = (raw) => {
    if (!raw) return {};
    if (typeof raw === 'string') {
        try {
            return JSON.parse(raw) || {};
        } catch {
            return {};
        }
    }
    if (typeof raw === 'object' && !Array.isArray(raw)) {
        return { ...raw };
    }
    return {};
};

export const getValueProperty = (value, key, fallback = '') => {
    const props = value?.propertyValues || {};
    if (props[key] !== undefined && props[key] !== null && props[key] !== '') {
        return props[key];
    }
    return fallback;
};

export const getSchemaField = (schema = [], type) =>
    (schema || []).find(item => item.type === type) || null;

export const CONTROL_DATA_SLOTS = {
    Dropdown: [],
    Radio: [],
    Checkbox: [],
    MultiCheckbox: [],
    Slider: [],
    TextInput: [],
    NumberInput: [],
    TextArea: [],
    DatePicker: [],
    ColorPicker: [{ id: 'color', label: 'Color', types: ['color'] }],
    ImageDisplay: [{ id: 'image', label: 'Image', types: ['image', 'imageList', 'url'] }],
    ImageCarousel: [{ id: 'gallery', label: 'Gallery', types: ['imageList', 'image'] }],
    Swatches: [
        { id: 'color', label: 'Color', types: ['color'] },
        { id: 'image', label: 'Image', types: ['image', 'imageList', 'url'] },
        { id: 'price', label: 'Price', types: ['currency', 'number'] }
    ],
    ValueCards: [
        { id: 'image', label: 'Image', types: ['image', 'imageList'] },
        { id: 'price', label: 'Price', types: ['currency', 'number'] },
        { id: 'description', label: 'Description', types: ['text'] }
    ]
};

export const fieldsForSlot = (schema, slot) =>
    normalizeValueSchema(schema).filter(field => slot.types.includes(field.type));

const bindingKey = (feature, role) => {
    const bindings = feature?.dataBindings || feature?.property?.dataBindings || {};
    const key = bindings?.[role];
    return key ? String(key) : '';
};

const readBinding = (value, feature, role) => {
    const key = bindingKey(feature, role);
    if (!key) return undefined;
    const props = value?.propertyValues || {};
    return props[key];
};

export const getValueByType = (value, schema = [], type, fallback = '') => {
    const field = getSchemaField(schema, type);
    if (!field?.key) return fallback;
    return getValueProperty(value, field.key, fallback);
};

export const getValuePrice = (value, schema = [], feature) => {
    const bound = readBinding(value, feature, 'price');
    if (bound !== undefined && bound !== null && bound !== '') return bound;
    const currency = getValueByType(value, schema, 'currency', '');
    if (currency !== '') return currency;
    return getValueByType(value, schema, 'number', '');
};

export const normalizeTextList = (input) => {
    if (input === undefined || input === null || input === '') return [];
    const list = Array.isArray(input) ? input : [input];
    return list.map((item) => {
        if (item === null || item === undefined) return '';
        if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
            return String(item);
        }
        return String(item.text ?? item.label ?? item.value ?? '');
    });
};

export const getVisibleTextList = (input) =>
    normalizeTextList(input).map(item => item.trim()).filter(Boolean);

export const getCardExtraFields = (value, schema = []) => {
    const priceField = getSchemaField(schema, 'currency') || getSchemaField(schema, 'number');
    return (schema || [])
        .filter(def => def.type !== 'imageList' && def.type !== 'image')
        .filter(def => !priceField || def.key !== priceField.key)
        .map(def => ({ def, val: getValueProperty(value, def.key, isTextListType(def.type) ? [] : '') }))
        .filter(entry => {
            if (isTextListType(entry.def.type)) return getVisibleTextList(entry.val).length > 0;
            return entry.val !== '' && entry.val !== undefined && entry.val !== null;
        });
};

const asMediaItems = (input) => {
    if (!input) return [];
    const list = Array.isArray(input) ? input : [input];
    return list
        .map(item => {
            if (!item) return null;
            if (typeof item === 'string') return { url: item, label: '' };
            const url = item.url || item.Url || item.src || item.Src || '';
            if (!url) return null;
            return { url: String(url), label: String(item.label || item.Label || '') };
        })
        .filter(Boolean);
};

export const getValueGallery = (value, schema = [], feature) => {
    const props = value?.propertyValues || {};
    const bound = readBinding(value, feature, 'gallery') ?? readBinding(value, feature, 'image');
    if (bound !== undefined) {
        const boundItems = asMediaItems(bound);
        if (boundItems.length) return boundItems;
        if (typeof bound === 'string' && bound.trim()) return [{ url: bound, label: '' }];
    }
    const imageListKey = getSchemaField(schema, 'imageList')?.key;
    const imageKey = getSchemaField(schema, 'image')?.key;
    const fromList = asMediaItems(imageListKey ? props[imageListKey] : (props.gallery || props.images));
    if (fromList.length) return fromList;
    const fromImage = asMediaItems(imageKey ? props[imageKey] : props.heroImage);
    if (fromImage.length) return fromImage;
    if (value?.imageUrl) return [{ url: value.imageUrl, label: '' }];
    return [];
};

export const getValueThumbnail = (value, schema = [], feature) => {
    const gallery = getValueGallery(value, schema, feature);
    if (gallery[0]?.url) return gallery[0].url;
    return value?.imageUrl || '';
};

export const getValueSwatchColor = (value, schema = [], feature) => {
    const bound = readBinding(value, feature, 'color');
    const raw = bound !== undefined && bound !== null && bound !== ''
        ? bound
        : getValueByType(value, schema, 'color', value?.propertyValues?.swatch || '');
    return typeof raw === 'string' ? raw : '';
};

export const isSystemFeature = (feature) => {
    const flag = feature?.isSystem
        ?? feature?.property?.isSystem
        ?? feature?.attributes?.isSystem
        ?? feature?.Attributes?.isSystem;
    return flag === true || flag === 'true';
};

export const isOutputFeature = (feature) => {
    const flag = feature?.isOutput
        ?? feature?.IsOutput
        ?? feature?.property?.isOutput
        ?? feature?.property?.IsOutput;
    return flag === true || flag === 'true';
};

export const resolveCarouselFeature = (features, bindFeatureName) => {
    const list = features || [];
    if (bindFeatureName) {
        return list.find(feature => feature.name === bindFeatureName) || null;
    }
    return list.find(feature => String(feature.inputType || feature.property?.inputType || '').toLowerCase() === 'imagecarousel')
        || list.find(feature => isSystemFeature(feature))
        || null;
};

export const formatCurrency = (value) => {
    if (value === undefined || value === null || value === '') return '';
    const number = Number(String(value).replace(/[^\d.-]/g, ''));
    if (!Number.isFinite(number)) return String(value);
    return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', maximumFractionDigits: 0 }).format(number);
};
